// Typed client for the Brand-Dashboard Cloudflare Worker admin endpoints.
// /api/admin/* routes are gated by X-Admin-Secret (VITE_WORKER_ADMIN_SECRET) AND
// the signed-in admin's Supabase JWT — always call them via workerAuthedFetch.
// Base URL is VITE_WORKER_URL (e.g. https://bmc-intel-worker.mikerama94.workers.dev).

import { supabase } from './supabase'

const WORKER_BASE = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? ''
const ADMIN_SECRET = (import.meta.env.VITE_WORKER_ADMIN_SECRET as string | undefined) ?? ''

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Who a (re-)blast reaches.
 *   'new_only' — creators never contacted for this campaign (default).
 *   'all'      — also re-emails previous recipients and prior applicants.
 */
export type BlastAudienceMode = 'new_only' | 'all'

export interface BlastCampaignResult {
  ok?: boolean
  dry_run?: boolean
  forced?: boolean
  category: string
  tiktok_categories: string[]
  matched_creators: number
  already_applied: number
  already_blasted?: number
  applied_and_blasted?: number
  gender_filter?: string | null
  gender_excluded?: number
  eligible_recipients: number
  // audience: 'all' re-includes previous recipients + prior applicants.
  // repeat_recipients is the union of the two breakdowns (someone both blasted
  // and applied counts once), so prefer it for "how many get a second email".
  audience?: BlastAudienceMode
  // Matched creators contacted before by either route (union, mode-independent).
  // The per-filter counts above are measured sequentially and can't be summed.
  previously_contacted?: number
  reblasted_recipients?: number
  reapplied_recipients?: number
  repeat_recipients?: number
  // manual creator selection (staging test tool)
  manual_select?: boolean
  selected_count?: number
  queued?: number
  skipped?: number
  enable_outbound_email?: boolean
  note?: string
  // error shape (non-2xx)
  error?: string
  code?: string
}

export interface BlastCampaignOptions {
  force?: boolean
  dry_run?: boolean
  // When set (and non-empty), the blast targets EXACTLY these roster creators,
  // bypassing category + gender targeting. Staging-only test path.
  creator_ids?: string[]
  // Defaults to 'new_only' on the worker when omitted.
  audience?: BlastAudienceMode
}

export interface EmailQueueRow {
  id: string
  campaign_id: string | null
  email_type: string
  status: string
  recipient_email: string
  attempts: number
  last_error: string | null
  sent_at: string | null
  last_attempt_at: string | null
  scheduled_for: string
}

export interface BlastStatusResult {
  enable_outbound_email: boolean
  queue: {
    queued: number
    sending: number
    failed: number
    skipped: number
  }
}

// ── Internal fetch helpers ───────────────────────────────────────────────────

// Every /api/admin/* route (except the public mirror-image proxy, which this
// client never calls) requires the admin JWT as well as the secret, so the
// GET/POST shorthands go through workerAuthedFetch rather than a secret-only fetch.
function workerPost<T>(path: string, body?: unknown): Promise<T> {
  return workerAuthedFetch<T>(path, {
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
}

// For public worker endpoints that don't require X-Admin-Secret
async function workerPublicPost<T>(path: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  if (!WORKER_BASE) throw new Error('VITE_WORKER_URL is not configured in .env')
  const res = await fetch(`${WORKER_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...extraHeaders },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  const data = await res.json() as T
  if (!res.ok) {
    const errData = data as { error?: string }
    throw new Error(errData.error ?? `Worker responded ${res.status}`)
  }
  return data
}

function workerGet<T>(path: string): Promise<T> {
  return workerAuthedFetch<T>(path, { method: 'GET' })
}

/**
 * Admin-secret PLUS the signed-in admin's Supabase JWT.
 *
 * For endpoints where "which app called" isn't enough and the worker has to know
 * WHICH ADMIN is acting — anything attributable, and anything that moves money.
 * The secret ships inside this bundle, so on its own it identifies the app to
 * every one of its users equally; the JWT is the part that can't be lifted out
 * of the source. The worker verifies the token against Supabase and reads the
 * actor from it, never from the request body.
 */
export async function workerAuthedFetch<T>(path: string, init: RequestInit): Promise<T> {
  if (!WORKER_BASE) throw new Error('VITE_WORKER_URL is not configured in .env')
  if (!ADMIN_SECRET) throw new Error('VITE_WORKER_ADMIN_SECRET is not configured in .env')

  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error('Your session has expired — sign out and back in, then retry.')

  const res = await fetch(`${WORKER_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Secret': ADMIN_SECRET,
      'Authorization': `Bearer ${token}`,
      ...init.headers,
    },
  })

  const data = await res.json().catch(() => ({})) as T
  if (!res.ok) {
    const errData = data as { error?: string; code?: string }
    const msg = errData.error ?? `Worker responded ${res.status}`
    throw Object.assign(new Error(msg), { code: errData.code, status: res.status })
  }
  return data
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * GET /api/admin/email-queue
 * Returns recent queue rows for inspection. All params optional.
 */
export function getEmailQueue(params: {
  campaign_id?: string
  status?: string
  scheduled_from?: string
  scheduled_to?: string
  recipient_email?: string
  limit?: number
} = {}): Promise<{ rows: EmailQueueRow[] }> {
  const p = new URLSearchParams()
  if (params.campaign_id)     p.set('campaign_id',     params.campaign_id)
  if (params.status)          p.set('status',          params.status)
  if (params.scheduled_from)  p.set('scheduled_from',  params.scheduled_from)
  if (params.scheduled_to)    p.set('scheduled_to',    params.scheduled_to)
  if (params.recipient_email) p.set('recipient_email', params.recipient_email)
  if (params.limit)           p.set('limit',           String(params.limit))
  const qs = p.toString()
  return workerGet<{ rows: EmailQueueRow[] }>(`/api/admin/email-queue${qs ? `?${qs}` : ''}`)
}

export interface OutreachJoinUrlResult {
  /** The Trybe invite a new run gets unless the admin overrides it. */
  url: string
  /** 'env' = the deployed OUTREACH_TRYBE_JOIN_URL binding, 'default' = the
   *  value compiled into the worker. Worth showing: it answers "where did
   *  this link even come from?" without opening wrangler. */
  source: 'env' | 'default'
}

/**
 * GET /api/admin/outreach/join-url
 *
 * The Trybe link the outreach cron would send. Read from the worker rather
 * than hardcoded here so the confirm dialog can never show one invite while
 * the run sends another — the worker is the only place the default lives.
 */
export function getOutreachJoinUrl(): Promise<OutreachJoinUrlResult> {
  return workerGet<OutreachJoinUrlResult>('/api/admin/outreach/join-url')
}

/**
 * GET /api/admin/blast-status
 * Returns whether outbound email is enabled and current queue depth.
 */
export function getBlastStatus(): Promise<BlastStatusResult> {
  return workerGet<BlastStatusResult>('/api/admin/blast-status')
}

/**
 * POST /api/admin/blast-campaign/:id
 * Queues campaign blast emails for all newly-eligible creators.
 * Pass force=true to re-blast a campaign that already ran (only new creators
 * are queued; prior recipients are excluded by deduplication on the worker).
 */
export function blastCampaign(
  campaignId: string,
  opts: BlastCampaignOptions = {},
): Promise<BlastCampaignResult> {
  return workerPost<BlastCampaignResult>(`/api/admin/blast-campaign/${campaignId}`, opts)
}

/**
 * Same as blastCampaign with dry_run=true — returns counts without mutating
 * the queue or campaign status.
 */
export function dryRunBlast(campaignId: string, opts: Omit<BlastCampaignOptions, 'dry_run'> = {}): Promise<BlastCampaignResult> {
  return blastCampaign(campaignId, { ...opts, dry_run: true })
}

// ── Hire refunds (brand wallet credit) ───────────────────────────────────────

export interface RefundHireResult {
  hire_id: string
  brand_id?: string
  ledger_id: string
  refunded_cents: number
  refunded_total_cents?: number
  collected_cents?: number
  fully_refunded?: boolean
  hire_status?: string
  videos_delivered?: number
  videos_required?: number
  balance_cents?: number
  /** true = this exact refund was already applied; no new credit was created. */
  replayed: boolean
}

export interface RefundHireOptions {
  /** Omit to let the worker default to the pro-rata undelivered share. */
  amount_cents?: number
  reason?: string
  /** Ops exception: refund a hire whose deadline hasn't passed yet. */
  allow_before_deadline?: boolean
  /**
   * Collapses a replayed request (double-click, retry) into a no-op. Generate
   * ONE key per intended refund and reuse it across retries of that refund.
   */
  idempotency_key?: string
}

/**
 * POST /api/admin/hires/:id/refund-to-credit
 *
 * Refunds an undelivered hire to the brand's wallet as credit — no card refund.
 * Eligibility (money collected, delivery incomplete, deadline passed, amount
 * within what's left) is enforced in the database, so a rejected refund comes
 * back as a 409 with the specific rule that failed.
 */
export function refundHireToCredit(
  hireId: string,
  adminEmail: string,
  opts: RefundHireOptions = {},
): Promise<RefundHireResult> {
  return workerPost<RefundHireResult>(`/api/admin/hires/${hireId}/refund-to-credit`, {
    ...opts,
    admin_email: adminEmail,
  })
}
// ── Whop subscription payments (Accounts & Finance) ──────────────────────────

export interface WhopSubscriptionPaymentRow {
  payment_id: string | null
  status: string | null
  substatus: string | null
  /**
   * What the brand actually paid, from Whop's discount-and-tax-aware
   * settlement_amount. `null` means the per-payment lookup failed — render it
   * as unavailable, never as $0 or list price.
   */
  amount_cents: number | null
  currency: string | null
  refunded_amount_cents: number | null
  created_at: string | null
  paid_at: string | null
  plan_id: string | null
  tier: 'free' | 'starter' | 'pro' | 'pro_annual' | 'agency' | null
  product_id: string | null
  product_title: string | null
  brand_id: string | null
  account_id: string | null
  brand_name: string | null
  account_name: string | null
  user_name: string | null
  user_email: string | null
  promo_code: string | null
  promo_amount_off: number | null
  /** Whop's processing fee for this charge (settlement_amount minus amount_after_fees). */
  fee_cents: number | null
  /**
   * Whop's own reason enum: 'subscription_create' | 'subscription_cycle' |
   * 'subscription_update' | 'one_time' | 'manual' | 'subscription'.
   * 'subscription_cycle' is the renewal signal — an auto-charge on an existing
   * subscription, as opposed to the first charge on signup/upgrade.
   */
  billing_reason: string | null
  payment_method_type: string | null
  card_brand: string | null
  card_last4: string | null
}

export interface WhopSubscriptionScanDiagnostics {
  matched_by_product: number
  matched_by_plan: number
  excluded_non_subscription_product: number
  /**
   * Whop returned payments on a product/plan id we don't recognise. Non-zero
   * means a tier is likely missing from the report because its id isn't in
   * wrangler.toml — compare seen_product_ids/seen_plan_ids against the config.
   */
  excluded_unrecognised: number
  /**
   * Trial-start placeholder invoices where nothing was ever owed (subtotal and
   * charged amount both zero). Whop's own dashboard omits these. A COMPED
   * subscription — real subtotal reduced to $0 by a 100%-off promo — is NOT
   * counted here and IS included in the report.
   */
  excluded_zero_value: number
  /** Whop returned a payment with no id, so it couldn't be deduped or enriched. */
  dropped_missing_id: number
  /**
   * Rows summed across every scan pass, pre-filter. Passes overlap by design,
   * so this exceeds the kept total even when nothing is excluded — use
   * distinct_returned to compare against a Whop dashboard count.
   */
  raw_returned: number
  /** Distinct payment ids Whop returned, pre-filter. */
  distinct_returned: number
  raw_by_product: Record<string, number>
  kept_by_product: Record<string, number>
  kept_by_plan: Record<string, number>
  kept_by_status: Record<string, number>
  /** One entry per scan pass (one filter id each) — returned vs newly unique. */
  passes: { filter: string; returned: number; new_unique: number }[]
  seen_product_ids: string[]
  seen_plan_ids: string[]
  /** Only when requested with include_ids=1 — kept payment ids per plan, for
   *  diffing against a Whop dashboard CSV export. */
  payment_ids_by_plan?: Record<string, string[]>
}

export interface WhopSubscriptionPaymentsResult {
  rows: WhopSubscriptionPaymentRow[]
  total: number
  /** More subscription payments exist on Whop than were scanned — `total` is a floor. */
  truncated: boolean
  scanned_plan_ids: string[]
  scanned_product_ids: string[]
  diagnostics: WhopSubscriptionScanDiagnostics
}

export interface WhopSubscriptionStatsResult {
  total_count: number
  truncated: boolean
  /** Every status's count at once — this call never filters by status, so the
   *  tab badges stay correct no matter which tab is currently active. */
  status_counts: Record<string, number>
  stats: {
    /** 'paid' charges only — the rest never collected anything (or, for
     *  void, gave it back), so they're excluded from every total here. */
    total_amount_cents: number
    brand_amount_cents: number
    agency_amount_cents: number
    /** How many 'paid' charges make up each amount above — same paid-only
     *  population, not the raw row count (that's total_count above, which
     *  includes every status). */
    total_paid_count: number
    brand_paid_count: number
    agency_paid_count: number
  }
}

/**
 * GET /api/admin/whop-subscription-payments
 * Brand-dashboard subscription charges, live from Whop, scoped to our
 * subscription plan ids (excludes the launch product and creator-hire charges).
 */
// No search or date-range params: Whop's v2 API exposes neither server-side,
// and building them client-side (fetch everything, filter in code) was tried
// and removed — see listSubscriptionPayments in the worker for why. `statuses`
// is the one filter kept, because it's the one Whop actually applies
// server-side.
export function getWhopSubscriptionPayments(params: {
  limit?: number
  offset?: number
  statuses?: string[]
  max_scan?: number
} = {}): Promise<WhopSubscriptionPaymentsResult> {
  const p = new URLSearchParams()
  if (params.limit)          p.set('limit',          String(params.limit))
  if (params.offset)         p.set('offset',         String(params.offset))
  if (params.statuses?.length) p.set('statuses',     params.statuses.join(','))
  if (params.max_scan)       p.set('max_scan',       String(params.max_scan))
  const qs = p.toString()
  return workerGet<WhopSubscriptionPaymentsResult>(
    `/api/admin/whop-subscription-payments${qs ? `?${qs}` : ''}`
  )
}

/**
 * GET /api/admin/whop-subscription-stats
 * Revenue totals + status-tab counts, live from Whop. Deliberately separate
 * from getWhopSubscriptionPayments — call both in parallel (own useQuery
 * each) so a heavy stats scan never blocks the table from rendering. Ignores
 * `statuses`: this always scans every status, since the tab badges need every
 * status's count regardless of which tab is active.
 */
// No search or date-range params — same reasoning as getWhopSubscriptionPayments.
export function getWhopSubscriptionStats(params: {
  max_scan?: number
} = {}): Promise<WhopSubscriptionStatsResult> {
  const p = new URLSearchParams()
  if (params.max_scan) p.set('max_scan', String(params.max_scan))
  const qs = p.toString()
  return workerGet<WhopSubscriptionStatsResult>(
    `/api/admin/whop-subscription-stats${qs ? `?${qs}` : ''}`
  )
}

// ── Creator-hire payments (Whop, Hiring tab) ─────────────────────────────────

export interface WhopHirePaymentRow {
  payment_id: string | null
  status: string | null
  substatus: string | null
  amount_cents: number | null
  currency: string | null
  refunded_amount_cents: number | null
  created_at: string | null
  paid_at: string | null
  /** Whop's processing fee on this charge (settlement_amount − amount_after_fees). */
  fee_cents: number | null
  /** cr_hires.creator_rate_cents — what the creator is owed for this hire. */
  creator_amount_cents: number | null
  /** BMC's realised take: the hire's quoted margin (cr_hires.bmc_fee_cents)
   *  less Whop's fee on this charge. Null unless the charge actually settled
   *  (`status === 'paid'`) and both inputs resolved — BMC keeps nothing on a
   *  failed charge. NOT derived from amount_cents: wallet credit part-funds
   *  hires, so the Whop charge is only the uncovered remainder. */
  platform_fee_cents: number | null
  payment_method_type: string | null
  card_brand: string | null
  card_last4: string | null
  brand_id: string | null
  brand_name: string | null
  hire_id: string | null
  campaign_id: string | null
  creator_name: string | null
  creator_tiktok_username: string | null
  campaign_name: string | null
  product_name: string | null
  package_size: number | null
  /** cr_hires.status at the time of this fetch — 'confirmed'|'completed'|
   *  'partial_completed'|'failed'|'cancelled'|... — not the Whop payment
   *  status above, which is a different lifecycle entirely. */
  hire_status: string | null
}

export interface WhopHirePaymentsResult {
  rows: WhopHirePaymentRow[]
  total: number
  truncated: boolean
}

/**
 * GET /api/admin/whop-hire-payments
 * Creator-hire charges, live from Whop — the Hiring tab's counterpart to
 * getWhopSubscriptionPayments. No search or date-range params, same reasoning
 * (see getWhopSubscriptionPayments): `statuses` is the only filter Whop
 * applies server-side.
 */
export function getWhopHirePayments(params: {
  limit?: number
  offset?: number
  statuses?: string[]
  max_scan?: number
} = {}): Promise<WhopHirePaymentsResult> {
  const p = new URLSearchParams()
  if (params.limit)          p.set('limit',          String(params.limit))
  if (params.offset)         p.set('offset',         String(params.offset))
  if (params.statuses?.length) p.set('statuses',     params.statuses.join(','))
  if (params.max_scan)       p.set('max_scan',       String(params.max_scan))
  const qs = p.toString()
  return workerGet<WhopHirePaymentsResult>(
    `/api/admin/whop-hire-payments${qs ? `?${qs}` : ''}`
  )
}

export interface WhopHireStatsResult {
  total_count: number
  truncated: boolean
  status_counts: Record<string, number>
  stats: {
    total_amount_cents: number
    total_fee_cents: number
    /** BMC's realised take across all SETTLED hires: each hire's quoted margin
     *  less Whop's fee on that charge, summed. Covers hires that reached Whop
     *  only — one fully covered by wallet credit never creates a Whop payment,
     *  so it isn't represented here. */
    total_platform_fee_cents: number
    /** Count behind total_amount_cents — 'paid' rows only. fee_unavailable_count
     *  and platform_fee_unavailable_count are both subsets of this. */
    total_paid_count: number
    /** Non-zero means total_fee_cents is a floor, not exact — a per-payment
     *  lookup failed for that many rows during the full-scan enrichment. */
    fee_unavailable_count: number
    /** Non-zero means total_platform_fee_cents is a floor — that many paid
     *  charges had no resolvable hire margin (typically a deleted hire row). */
    platform_fee_unavailable_count: number
  }
}

/**
 * GET /api/admin/whop-hire-stats
 * Revenue + Whop-fee totals for the Hiring tab. Unlike the subscription tab's
 * stats, this genuinely enriches the FULL scanned set (fee has no v2
 * equivalent at all), so it's slower — measured ~30s at current volume
 * (~250 hire payments). Cache aggressively on the caller side; this isn't
 * meant to be refetched on every tab switch.
 */
export function getWhopHireStats(params: {
  max_scan?: number
} = {}): Promise<WhopHireStatsResult> {
  const p = new URLSearchParams()
  if (params.max_scan) p.set('max_scan', String(params.max_scan))
  const qs = p.toString()
  return workerGet<WhopHireStatsResult>(
    `/api/admin/whop-hire-stats${qs ? `?${qs}` : ''}`
  )
}

export interface CreditHirePaymentRow {
  id: string
  brand_id: string
  brand_name: string | null
  creator_id: string
  creator_name: string | null
  creator_tiktok_username: string | null
  campaign_id: string
  campaign_name: string | null
  product_name: string | null
  package_size: number | null
  /** cr_hires.status lifecycle — same enum as the Whop hire tab's hire_status. */
  status: string
  brand_rate_cents: number | null
  creator_rate_cents: number | null
  bmc_fee_cents: number | null
  credit_applied_cents: number
  card_charged_cents: number
  refunded_credit_cents: number
  refundable_cents: number
  /** 'credit' = fully wallet-funded, never touched Whop. 'mixed' = credit +
   *  card — also has a (partial) row on the Whop Hire Payments tab. Never
   *  'card' here; this tab only ever returns rows with credit_applied_cents > 0. */
  funding_source: 'credit' | 'mixed' | null
  /** BMC's take. EXACT on a credit-only hire — it never reached Whop, so there
   *  is no processing fee to net off, unlike the Whop tab where every row
   *  settled on a card. GROSS on a 'mixed' hire: Whop's cut of the card half
   *  isn't in our DB, so treat those as an upper bound. */
  platform_fee_cents: number
  confirmed_at: string | null
  created_at: string
}

export interface CreditHirePaymentsResult {
  rows: CreditHirePaymentRow[]
  total: number
}

/**
 * GET /api/admin/credit-hire-payments
 * Hires funded (fully or partly) by wallet credit — read straight from
 * cr_hires, no Whop call. Covers hires the Whop-sourced Hiring Transactions
 * tab can't see at all: one fully paid by credit never creates a Whop
 * payment, so it's absent there entirely.
 */
export function getCreditHirePayments(params: {
  limit?: number
  offset?: number
  statuses?: string[]
} = {}): Promise<CreditHirePaymentsResult> {
  const p = new URLSearchParams()
  if (params.limit)          p.set('limit',   String(params.limit))
  if (params.offset)         p.set('offset',  String(params.offset))
  if (params.statuses?.length) p.set('statuses', params.statuses.join(','))
  const qs = p.toString()
  return workerGet<CreditHirePaymentsResult>(
    `/api/admin/credit-hire-payments${qs ? `?${qs}` : ''}`
  )
}

export interface CreditHireStatsResult {
  total_count: number
  /** Every status's count, unfiltered — the tab badges, including statuses
   *  that no longer have their own tab (declined, expired_offer, ...). */
  status_counts: Record<string, number>
  /** Successful hires ('confirmed' | 'completed' | 'partial_completed') only
   *  — same population as every field under `stats` below. */
  funding_counts: { credit: number; mixed: number }
  stats: {
    /** Gross value of these hires (what the full package would have cost).
     *  Successful hires only — declined/expired/cancelled/failed hires
     *  (which can carry stale pre-refund amounts on the row, or none at all)
     *  never contribute to this or any total below. */
    total_brand_rate_cents: number
    total_credit_applied_cents: number
    /** The card-charged remainder on 'mixed' hires only. */
    total_card_charged_cents: number
    total_refunded_credit_cents: number
    /** BMC's take on credit-ONLY hires. Exact: no Whop charge means no
     *  processing fee to deduct. */
    total_platform_fee_credit_only_cents: number
    /** BMC's take on 'mixed' hires, GROSS — Whop's cut on their card half
     *  isn't in our DB, so this is an upper bound, not realised revenue. */
    total_platform_fee_mixed_gross_cents: number
    /** Count behind every total above. */
    total_successful_count: number
  }
}

/**
 * GET /api/admin/credit-hire-stats
 * Totals for the Credit Hires tab. Unlike the Whop hire tab's stats call,
 * this is a single cheap DB query (no per-row enrichment), so it doesn't need
 * the aggressive caching that one requires.
 */
export function getCreditHireStats(): Promise<CreditHireStatsResult> {
  return workerGet<CreditHireStatsResult>('/api/admin/credit-hire-stats')
}

/**
 * GET /api/admin/whop-subscription-reconcile
 * Diffs the live Whop scan against the webhook-written subscription ledger to
 * find payments Whop's list endpoint withholds, and fetches a sample of them by
 * id to reveal the status/substatus responsible. Read-only diagnosis.
 */
export function reconcileWhopSubscriptionPayments(params: {
  max_scan?: number
  inspect?: number
} = {}): Promise<Record<string, unknown>> {
  const p = new URLSearchParams()
  if (params.max_scan) p.set('max_scan', String(params.max_scan))
  if (params.inspect)  p.set('inspect',  String(params.inspect))
  const qs = p.toString()
  return workerGet<Record<string, unknown>>(
    `/api/admin/whop-subscription-reconcile${qs ? `?${qs}` : ''}`
  )
}

// ── System subscription payments (our DB, not live Whop) ─────────────────────

export interface SystemSubscriptionPaymentRow {
  id: string
  source: 'brand' | 'account'
  whop_payment_id: string
  whop_membership_id: string | null
  brand_id: string | null
  account_id: string | null
  brand_name: string | null
  account_name: string | null
  /** brand_configs.contact_email ?? delivery_email. */
  brand_email: string | null
  /** The agency account has no email column — this is the owner's
   *  profiles.email, resolved via accounts.owner_user_id. */
  account_email: string | null
  plan_tier: string | null
  amount_cents: number
  currency: string
  /** 'succeeded' | 'refunded' | 'failed' | 'disputed' — the DB's own enum. */
  status: string
  paid_at: string
  created_at: string
  promo_code: string | null
  promo_type: string | null
  promo_amount_off: number | null
}

export interface SystemSubscriptionPaymentsResult {
  rows: SystemSubscriptionPaymentRow[]
  total: number
}

export interface SystemSubscriptionStatsResult {
  total_count: number
  /** Every status's count at once — this call never filters by status, so the
   *  tab badges stay correct no matter which tab is currently active. */
  status_counts: Record<string, number>
  stats: {
    total_succeeded_cents: number
    /** Count behind total_succeeded_cents — 'succeeded' rows only. */
    total_succeeded_count: number
    total_refunded_cents: number
    total_failed_cents: number
    by_tier: Record<string, { count: number; amount_cents: number }>
    /** Both `count` and `amount_cents` are 'succeeded' rows only — matches the
     *  "Succeeded revenue by source" label this renders under. */
    by_source: Record<'brand' | 'account', { count: number; amount_cents: number }>
  }
  scanned: { brand_rows: number; account_rows: number }
}

/**
 * GET /api/admin/system-subscription-payments
 * Subscription charges as OUR DB recorded them (webhook-written), not live
 * from Whop. Fast enough for real revenue totals over the full filtered set —
 * unlike the Whop tab, where only the current page carries a verified amount.
 * Only as complete as the webhook's own coverage; use
 * reconcileWhopSubscriptionPayments to check for gaps.
 */
export function getSystemSubscriptionPayments(params: {
  limit?: number
  offset?: number
  statuses?: string[]
  created_after?: string
  created_before?: string
  search?: string
} = {}): Promise<SystemSubscriptionPaymentsResult> {
  const p = new URLSearchParams()
  if (params.limit)          p.set('limit',          String(params.limit))
  if (params.offset)         p.set('offset',         String(params.offset))
  if (params.statuses?.length) p.set('statuses',     params.statuses.join(','))
  if (params.created_after)  p.set('created_after',  params.created_after)
  if (params.created_before) p.set('created_before', params.created_before)
  if (params.search)         p.set('search',         params.search)
  const qs = p.toString()
  return workerGet<SystemSubscriptionPaymentsResult>(
    `/api/admin/system-subscription-payments${qs ? `?${qs}` : ''}`
  )
}

/**
 * GET /api/admin/system-subscription-stats
 * Revenue totals + status-tab counts, from our DB. Deliberately separate from
 * getSystemSubscriptionPayments — call both in parallel (own useQuery each).
 * Ignores `statuses`: always scans every status, since the tab badges need
 * every status's count regardless of which tab is active.
 */
export function getSystemSubscriptionStats(params: {
  created_after?: string
  created_before?: string
  search?: string
} = {}): Promise<SystemSubscriptionStatsResult> {
  const p = new URLSearchParams()
  if (params.created_after)  p.set('created_after',  params.created_after)
  if (params.created_before) p.set('created_before', params.created_before)
  if (params.search)         p.set('search',         params.search)
  const qs = p.toString()
  return workerGet<SystemSubscriptionStatsResult>(
    `/api/admin/system-subscription-stats${qs ? `?${qs}` : ''}`
  )
}

export interface IntelReportResult {
  id: string
  brief_markdown: string
  total_videos_analyzed: number
  generated_at: string
}

/**
 * POST /api/briefs/generate
 * Triggers intel report generation for a brand. Long-running (1-2 min) — call
 * without awaiting for fire-and-forget behaviour from the admin dashboard.
 */
export function generateIntelReport(brandId: string, authToken?: string): Promise<IntelReportResult> {
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined
  return workerPublicPost<IntelReportResult>('/api/briefs/generate', { brand_id: brandId }, headers)
}

// ── Creator-reported sample problems ─────────────────────────────────────────

export interface ResolveSampleProblemResult {
  hire: Record<string, unknown>
  also_applied_to_hires: string[]
}

/**
 * POST /api/admin/hires/:id/sample-problem/resolve  (X-Admin-Secret + admin JWT)
 * Closes a creator-reported sample problem and marks the sample received. The
 * worker attributes it to the signed-in admin and records it on the brand's
 * audit trail; `note` (optional) is shown to the brand next to the problem.
 */
export function resolveSampleProblem(hireId: string, note?: string): Promise<ResolveSampleProblemResult> {
  return workerAuthedFetch<ResolveSampleProblemResult>(
    `/api/admin/hires/${encodeURIComponent(hireId)}/sample-problem/resolve`,
    { method: 'POST', body: JSON.stringify({ note: note?.trim() || undefined }) },
  )
}

/**
 * POST /api/admin/hires/:id/sample-receipt  (X-Admin-Secret + admin JWT)
 * Records the "did your sample arrive?" answer on the creator's behalf — for
 * creators who told support instead of using the app. Only works while the
 * creator hasn't answered; stamped as an admin answer on the brand's audit trail.
 */
export function setSampleReceiptForCreator(
  hireId: string,
  answer: { status: 'received' } | { status: 'problem'; problem_type: string },
  note?: string,
): Promise<{ hire: Record<string, unknown> }> {
  return workerAuthedFetch<{ hire: Record<string, unknown> }>(
    `/api/admin/hires/${encodeURIComponent(hireId)}/sample-receipt`,
    { method: 'POST', body: JSON.stringify({ ...answer, note: note?.trim() || undefined }) },
  )
}

// ── BMC Assistant kill switch ────────────────────────────────────────────────

/**
 * GET /api/support/ai-status  (public, no auth)
 * Whether SUPPORT_AI_ENABLED is on in the worker. The flag lives only in the
 * worker env, so the Support Tickets page asks here before showing its AI
 * queues and AI handoffs button. Any failure reads as "off".
 */
export async function fetchSupportAiEnabled(): Promise<boolean> {
  if (!WORKER_BASE) return false
  try {
    const res = await fetch(`${WORKER_BASE}/api/support/ai-status`)
    if (!res.ok) return false
    const body = (await res.json()) as { enabled?: unknown }
    return body.enabled === true
  } catch {
    return false
  }
}

export interface CreatorLoginLinkResult {
  /** One-time creator web portal sign-in URL (…/auth-callback?token_hash=…). */
  url: string
  /** The creator's canonical auth email the link was issued for. */
  email: string
}

/**
 * POST /api/admin/creators/:userId/portal-login  (X-Admin-Secret + admin JWT)
 * Mints a one-time sign-in link into the creator web portal for any creator,
 * with or without a Coach seat. Sends no email.
 */
export function createCreatorLoginLink(userId: string): Promise<CreatorLoginLinkResult> {
  return workerAuthedFetch<CreatorLoginLinkResult>(
    `/api/admin/creators/${encodeURIComponent(userId)}/portal-login`,
    { method: 'POST' },
  )
}

/** A creator's TikTok connection as the worker describes it — never raw tokens. */
export interface CreatorTikTokConnection {
  open_id: string | null
  seller_name: string | null
  seller_base_region: string | null
  user_type: string | null
  granted_scopes: string[]
  shop_count: number
  /** First 16 hex chars of SHA-256 — tells tokens apart without revealing them. */
  access_token_fingerprint: string | null
  refresh_token_fingerprint: string | null
  access_token_expires_at: string | null
  refresh_token_expires_at: string | null
  connected_at: string
  updated_at: string
}

/** GET /api/admin/creators/:userId/tiktok  (X-Admin-Secret + admin JWT) */
export function getCreatorTikTok(userId: string): Promise<{ connected: boolean; connection: CreatorTikTokConnection | null }> {
  return workerAuthedFetch(`/api/admin/creators/${encodeURIComponent(userId)}/tiktok`, { method: 'GET' })
}

/**
 * POST /api/admin/creators/:userId/tiktok/disconnect  (X-Admin-Secret + admin JWT)
 * Deletes the creator's TikTok token and clears their connected flags; returns
 * what the connection was, for the audit trail. The creator reconnects normally.
 */
export function disconnectCreatorTikTok(userId: string): Promise<{ ok: true; previous: CreatorTikTokConnection | null }> {
  return workerAuthedFetch(`/api/admin/creators/${encodeURIComponent(userId)}/tiktok/disconnect`, { method: 'POST' })
}
// ── Admin: change a user's email ─────────────────────────────────────────────
// Dual-gated like the credit routes (X-Admin-Secret + admin JWT) and checked
// against its own 'users.change_email' permission. The DB function does all the
// work in one transaction — see creator-app migration
// 20261014000003_admin_change_user_email.sql for what moves, what is left as
// history, and the sign-in paths it revokes.

export interface EmailChangeConflict {
  table: string;
  blocking: boolean;
  message?: string;
  other_user_id?: string;
  other_account_is_empty?: boolean | null;
  last_sign_in_at?: string | null;
  created_at?: string | null;
}

export interface EmailChangePreflight {
  ok: boolean;
  error?: string;
  message?: string;
  target?: {
    user_id: string;
    current_email: string;
    new_email: string;
    full_name: string | null;
    tiktok_handle: string | null;
    role: string | null;
    onboarding_complete: boolean | null;
    roster_creator_id: string | null;
    trolley_recipient_id: string | null;
    last_sign_in_at: string | null;
  };
  conflicts?: EmailChangeConflict[];
  blocking_count?: number;
  /** What the change would invalidate — surfaced so the dialog can say it. */
  will_revoke?: {
    pending_otp_codes: number;
    coach_sign_in_links: number;
    queued_emails: number;
  };
}

export interface EmailChangeResult {
  ok: boolean;
  user_id: string;
  old_email: string;
  new_email: string;
  touched: Record<string, number>;
}

/** Whether the signed-in admin may use the email-change action at all. */
export function getEmailChangeContext(): Promise<{ is_auth_admin: boolean; can_change_email: boolean }> {
  return workerAuthedFetch('/api/admin/users/email-change/context', { method: 'GET' });
}

/** Read-only conflict report. Always run this before offering the change. */
export function preflightEmailChange(userId: string, newEmail: string): Promise<EmailChangePreflight> {
  return workerAuthedFetch<EmailChangePreflight>('/api/admin/users/email-change/preflight', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, new_email: newEmail }),
  });
}

/** The write. `reason` is mandatory (>= 10 chars) and lands in admin_audit_log. */
export function changeUserEmail(args: {
  userId: string;
  newEmail: string;
  reason: string;
  allowTrolleyLinked?: boolean;
}): Promise<EmailChangeResult> {
  return workerAuthedFetch<EmailChangeResult>('/api/admin/users/email-change', {
    method: 'POST',
    body: JSON.stringify({
      user_id: args.userId,
      new_email: args.newEmail,
      reason: args.reason,
      allow_trolley_linked: args.allowTrolleyLinked === true,
    }),
  });
}

// ── Admin: change a creator's TikTok handle ──────────────────────────────────
// Same dual gate as the email routes, but its own 'users.change_tiktok_handle'
// permission. See creator-app migration
// 20261015000000_admin_change_tiktok_handle.sql for why it refuses when another
// roster row owns the handle (silent re-point onto that creator's jobs) and
// when the row is OAuth-linked (the daily refresh reverts it within 24h).

export interface HandleChangeConflict {
  table: string;
  blocking: boolean;
  message?: string;
  other_roster_id?: string;
  other_has_open_id?: boolean;
  other_hires?: number;
}

export interface HandleChangePreflight {
  ok: boolean;
  error?: string;
  message?: string;
  target?: {
    user_id: string;
    current_handle: string | null;
    new_handle: string;
    email: string | null;
    full_name: string | null;
    roster_creator_id: string | null;
    roster_username: string | null;
    creator_open_id: string | null;
    stats_refreshed_at: string | null;
    showcase_videos: number;
  };
  conflicts?: HandleChangeConflict[];
  blocking_count?: number;
  will_reset?: { showcase_videos: number };
  /** The account the handle actually resolves to on the TikTok Shop
   *  marketplace. found:false means the handle does not exist there. */
  lookup?: {
    found: boolean; open_id?: string; username?: string; nickname?: string | null;
    avatar_url?: string | null; creator_gmv?: number | null; follower_count?: number | null;
    code?: string;
  };
  resolved_open_id?: string | null;
  /** True when the resolved account is NOT the one currently stored — a switch
   *  to a different TikTok account rather than a rename of the same one. */
  account_switch?: boolean;
  active_work?: {
    live_hires: number; unpaid_completed: number;
    pending_applications: number; videos_in_review: number;
    brands: string[];
  };
  /** The creator's coach seat, if they have one. A separate identity with its
   *  own handle — never changed unless the admin opts in. */
  coach_seat?: {
    id: string;
    tiktok_username: string | null;
    onboarding_status: string | null;
    slug: string | null;
    differs_from_app: boolean;
    is_building: boolean;
  } | null;
}

export interface HandleChangeResult {
  ok: boolean;
  user_id: string;
  old_handle: string | null;
  new_handle: string;
  oauth_linked: boolean;
  touched: Record<string, unknown>;
}

export function getHandleChangeContext(): Promise<{ is_auth_admin: boolean; can_change_handle: boolean }> {
  return workerAuthedFetch('/api/admin/users/handle-change/context', { method: 'GET' });
}

export function preflightHandleChange(userId: string, newHandle: string): Promise<HandleChangePreflight> {
  return workerAuthedFetch<HandleChangePreflight>('/api/admin/users/handle-change/preflight', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, new_handle: newHandle }),
  });
}

export function changeTikTokHandle(args: {
  userId: string; newHandle: string; reason: string;
  /** Consent for moving the creator onto a DIFFERENT TikTok account. */
  allowAccountSwitch?: boolean;
  /** Opt-in. True queues a FULL coach rebuild (~15 min), exactly as the
   *  creator's own "Change TikTok account" does. */
  changeCoach?: boolean;
}): Promise<HandleChangeResult> {
  return workerAuthedFetch<HandleChangeResult>('/api/admin/users/handle-change', {
    method: 'POST',
    body: JSON.stringify({
      user_id: args.userId,
      new_handle: args.newHandle,
      reason: args.reason,
      allow_account_switch: args.allowAccountSwitch === true,
      change_coach: args.changeCoach === true,
    }),
  });
}
